import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { templeGuides } from '@/data/templeGuides';

export const TempleFAQ = ({ temple }) => {
  const guide = templeGuides[temple.id];
  const [openIndex, setOpenIndex] = useState(0);

  if (!guide || !guide.faqs?.length) return null;

  return (
    <section
      className="py-16 md:py-24 bg-saffron-50/40 border-t border-orange-100/60"
      data-testid="temple-faq-section"
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <div className="container mx-auto px-6 md:px-12 max-w-4xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center px-4 py-1 bg-saffron-100 text-saffron-700 font-outfit text-xs uppercase tracking-wider mb-4">
            <HelpCircle className="w-3.5 h-3.5 mr-2" />
            Frequently Asked Questions
          </div>
          <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-temple-slate mb-3">
            {temple.name} Jyotirlinga — Your Questions Answered
          </h2>
          <p className="font-outfit text-base text-gray-600 max-w-2xl mx-auto">
            Practical, spiritual and travel questions asked most often by first-time pilgrims to {temple.name}.
          </p>
        </div>

        <div className="space-y-3">
          {guide.faqs.map((faq, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className="bg-white border border-orange-100/70 overflow-hidden transition-shadow hover:shadow-sm"
                data-testid={`faq-item-${idx}`}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                  className="w-full flex items-start justify-between text-left p-5 md:p-6 group"
                  data-testid={`faq-question-${idx}`}
                  aria-expanded={isOpen}
                >
                  <span
                    className="font-cormorant text-lg md:text-xl font-semibold text-temple-slate pr-4 leading-snug"
                    itemProp="name"
                  >
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-saffron-600 flex-shrink-0 mt-1 transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <div className="overflow-hidden">
                    <div
                      className="px-5 md:px-6 pb-5 md:pb-6 font-outfit text-base text-gray-700 leading-relaxed border-t border-orange-100/70 pt-4"
                      data-testid={`faq-answer-${idx}`}
                      itemProp="text"
                    >
                      {faq.a}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Helper for JSON-LD FAQ schema
export const getFaqJsonLd = (temple) => {
  const guide = templeGuides[temple.id];
  if (!guide || !guide.faqs?.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: guide.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };
};
